/*
 * PAWFEEDER cart drawer. Owns the dialog shell, focus management, and the shared
 * cart runtime used by PDP add-to-cart and checkout shortcuts so the drawer,
 * header count, and cart page always reflect the same server state.
 *
 * The footer (cart tools, subtotal, terms box, View cart / Checkout) is
 * server-rendered once and never rebuilt here, so the Order Note / Shipping
 * Estimate / Discount Code panels keep their state across line changes. Only the
 * items list and the subtotal are re-rendered from /cart/change.js responses.
 *
 * The three cart tools themselves are driven by assets/pawfeeder-cart-tools.js
 * (document-level delegation), which also carries an applied discount code into
 * checkout through the `?discount=` query parameter.
 *
 * CHG-2026-029: the cart no longer gates checkout. The quantity ladder belongs to
 * the product page, where the tier the buyer picks is a variant carrying its own
 * unit price, so a line keeps the price of the tier it was bought under when the
 * buyer raises or lowers the count afterwards. Nothing here holds a count at a
 * rung, disables a control at a rung, or blocks the checkout button - the terms
 * box is an acknowledgement, not a gate.
 *
 * The one thing the rung is still read for is the note printed under a line's
 * price when the count sits below it. That note is a statement of fact, never a
 * gate. Its wording comes from `data-tier-note` on the dialog, which
 * snippets/pawfeeder-cart-drawer.liquid renders from locales/en.default.json, so
 * the sentence has a single source and the server-rendered list agrees with the
 * one built here.
 *
 * CHG-2026-035: a refused add is not always a no-op, so addItems() reads the
 * cart back before it rejects and hands the answer to the caller. Shopify
 * answers a quantity it will only partly take with 422 *after* putting what it
 * could in the cart, and the previous code turned every non-ok response into a
 * plain Error - so the page that owned the fetch printed a failure while the
 * cart had already changed underneath it. Belonging to the cart, the read-back
 * lives here rather than in the callers: the caller asks "did anything land?"
 * and gets a line, never a sentence to match on.
 */
(function () {
  'use strict';

  var I18N = window.PF_I18N || {};

  var drawer = document.querySelector('[data-pf-cart-drawer]');
  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var body = drawer ? drawer.querySelector('[data-pf-cart-drawer-body]') : null;
  var lastFocus = null;
  var tierNoteTemplate = (drawer && drawer.getAttribute('data-tier-note')) || '';

  function money(cents) {
    if (window.PawfeederUI && window.PawfeederUI.money) return window.PawfeederUI.money(cents, window.Shopify && window.Shopify.currency && window.Shopify.currency.active);
    return (Number(cents || 0) / 100).toFixed(2);
  }

  function escapeHtml(value) {
    var node = document.createElement('span');
    node.textContent = value == null ? '' : String(value);
    return node.innerHTML;
  }

  function updateCount(count) {
    document.querySelectorAll('[data-pf-cart-count]').forEach(function (node) {
      node.textContent = String(count);
    });
  }

  function lineMeta(item) {
    var parts = [];
    (item.options_with_values || []).forEach(function (option) {
      if (option && option.value) parts.push(option.value);
    });
    return parts.join(' / ');
  }

  /*
   * The ladder rung of the option this line was bought under, or -1 when the
   * product declares no quantity-style option at all. Display only: it decides
   * whether the note below the price appears and nothing else. Mirrors the
   * three-state parse in snippets/pawfeeder-moq.liquid, where the option value is
   * the rung itself - "50PCS" reads 50, "1,000pcs" reads 1000, and "Sample"
   * reads 0, which is a real answer meaning "no minimum" rather than a failure.
   */
  function tierFor(item) {
    var tier = -1;
    (item.options_with_values || []).forEach(function (option) {
      if (!option || !option.name) return;
      var name = String(option.name).toLowerCase();
      if (name.indexOf('quantity') === -1 && name.indexOf('qty') === -1) return;
      var head = String(option.value == null ? '' : option.value).replace(/,/g, '').toUpperCase().split('P')[0];
      var digits = head.replace(/[^0-9]/g, '');
      tier = digits ? Number(digits) : 0;
    });
    return tier;
  }

  function tierNote(item) {
    var tier = tierFor(item);
    if (!tierNoteTemplate || tier <= 0 || item.quantity >= tier) return '';
    return '<p class="pf-cart-drawer__tier-note" data-pf-line-tier-note>' +
      escapeHtml(tierNoteTemplate.replace('[tier]', String(tier))) + '</p>';
  }

  function lineMarkup(item) {
    var unit = Math.round(item.final_line_price / Math.max(1, item.quantity));
    var meta = lineMeta(item);
    var compare = '';
    if (item.original_line_price > item.final_line_price) {
      var originalUnit = Math.round(item.original_line_price / Math.max(1, item.quantity));
      compare = '<s class="pf-cart-drawer__item-compare" data-pf-line-compare>' + money(originalUnit) + '</s>';
    }
    return '<li class="pf-cart-drawer__item" data-pf-line data-line-key="' + escapeHtml(item.key) + '">' +
      (item.image ? '<a class="pf-cart-drawer__item-media" href="' + escapeHtml(item.url) + '"><img src="' + escapeHtml(item.image) + '" alt="" width="72" height="72" loading="lazy"></a>' : '') +
      '<div class="pf-cart-drawer__item-body">' +
        '<b class="pf-cart-drawer__item-title">' + escapeHtml(item.product_title) + '</b>' +
        (meta ? '<small class="pf-cart-drawer__item-meta">' + escapeHtml(meta) + '</small>' : '') +
        '<p class="pf-cart-drawer__item-price"><span data-pf-line-price>' + money(unit) + ' <small>' + (I18N.per_piece || '/ Piece') + '</small></span>' + compare + '</p>' +
        tierNote(item) +
        '<div class="pf-cart-drawer__item-controls">' +
          '<div class="pf-cart-drawer__stepper">' +
            '<button type="button" data-pf-line-minus aria-label="' + (I18N.decrease_quantity || 'Decrease quantity') + '">-</button>' +
            '<span class="pf-cart-drawer__qty" data-pf-line-qty>' + item.quantity + '</span>' +
            '<button type="button" data-pf-line-plus aria-label="' + (I18N.increase_quantity || 'Increase quantity') + '">+</button>' +
          '</div>' +
          '<button type="button" class="pf-cart-drawer__remove" data-pf-line-remove>' + (I18N.remove || 'Remove') + '</button>' +
        '</div>' +
      '</div>' +
      '</li>';
  }

  function emptyMarkup() {
    return '<div class="pf-cart-drawer__empty"><p>' + (I18N.cart_empty || 'Your cart is empty.') + '</p>' +
      '<a class="pf-button pf-button--primary" href="' + root + 'collections/all">' + (I18N.shop_wholesale || 'Shop wholesale') + '</a></div>';
  }

  function renderCart(cart) {
    updateCount(cart.item_count);
    if (!body) return;
    var itemsBox = body.querySelector('[data-pf-cart-items]');
    var footer = drawer.querySelector('.pf-cart-drawer__footer');
    if (!itemsBox) return;

    if (!cart.items || !cart.items.length) {
      itemsBox.innerHTML = emptyMarkup();
      if (footer) footer.hidden = true;
      return;
    }

    itemsBox.innerHTML = '<ul class="pf-cart-drawer__items">' + cart.items.map(lineMarkup).join('') + '</ul>';
    if (footer) {
      footer.hidden = false;
      var subtotal = footer.querySelector('[data-pf-cart-subtotal]');
      /*
       * PawfeederUI.money already renders the ISO code when show_currency_code is
       * on, so the old " + currencyCode()" here printed "USD 792.00 USD".
       * The server-rendered value in the snippet matches this output exactly.
       */
      if (subtotal) subtotal.textContent = money(cart.total_price);
    }
    if (window.PawfeederCartTools && window.PawfeederCartTools.sync) window.PawfeederCartTools.sync();
  }

  function fetchCart() {
    return fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (response) { return response.ok ? response.json() : null; })
      .catch(function () { return null; });
  }

  function refresh() {
    return fetchCart().then(function (cart) {
      if (cart) renderCart(cart);
      return cart;
    });
  }

  function changeLine(key, quantity) {
    return fetch(root + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    }).then(function (response) {
      if (!response.ok) throw new Error((I18N.update_error || 'The cart could not be updated.'));
      return response.json();
    }).then(function (cart) { renderCart(cart); return cart; });
  }

  function open(opener) {
    if (!drawer || !drawer.showModal) return;
    lastFocus = opener || document.activeElement;
    if (!drawer.open) drawer.showModal();
    var focusable = drawer.querySelector('a, button');
    if (focusable) focusable.focus();
  }

  function close() {
    if (drawer && drawer.open) drawer.close();
  }

  /*
   * The cart line the read-back holds for the first item that was asked for, or
   * null when the refusal left every one of those variants out of the cart. This
   * is what "did anything land?" means: a count read from the server, never the
   * wording of Shopify's sentence - `Only N items were added to your cart due to
   * availability.` and `The maximum quantity of this item is already in your
   * cart.` are the same question with the same answer here (CHG-2026-035).
   */
  function landedLine(cart, items) {
    if (!cart || !cart.items || !cart.items.length) return null;
    for (var i = 0; i < items.length; i += 1) {
      var wanted = String(items[i].id);
      for (var j = 0; j < cart.items.length; j += 1) {
        var line = cart.items[j];
        if (String(line.variant_id) !== wanted) continue;
        if (Number(line.quantity) > 0) return line;
      }
    }
    return null;
  }

  function addItems(items, options) {
    var settings = options || {};
    /* Both callers get the same drawer rule they had before: the PDP lets the
       dialog open after an add, the wishlist page asks for `openDrawer: false`
       because it navigates to /cart itself. */
    var wantsDrawer = settings.openDrawer !== false && (!drawer || drawer.dataset.openAfterAdd !== 'false');
    return fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (response) {
      if (response.ok) return response.json();
      /* Read the cart back before rejecting (CHG-2026-035). The refusal may have
         been partial, and even a full refusal is worth a repaint: the buyer is
         about to be told something about a cart, so the drawer, the header count
         and that sentence should all describe the same server state. */
      return response.json().catch(function () { return {}; }).then(function (payload) {
        return refresh().then(function (cart) {
          var line = landedLine(cart, items);
          var error = new Error(payload.description || payload.message || (I18N.add_error || 'This item could not be added.'));
          error.cart = cart;
          error.status = response.status;
          error.line = line;
          error.partial = Boolean(line);
          if (error.partial && wantsDrawer) open();
          throw error;
        });
      });
    }).then(function (result) {
      return refresh().then(function (cart) {
        if (wantsDrawer) open();
        return { added: result, cart: cart };
      });
    });
  }

  function report(message) {
    if (window.PawfeederUI && window.PawfeederUI.showToast) window.PawfeederUI.showToast(message);
  }

  if (drawer && body) {
    document.querySelectorAll('[data-pf-cart-open]').forEach(function (button) {
      /*
       * Open first, reconcile after (CHG-2026-051). The drawer is rendered by
       * snippets/pawfeeder-cart-drawer.liquid on every page load, so showModal()
       * already reveals the right lines, quantities, prices and subtotal.
       *
       * This used to read `refresh().then(function () { open(button); })`, which
       * put a `cart.js` round trip in front of the dialog. Nothing on the click
       * path can start until that request settles, and the request is not
       * cacheable, so the drawer appeared only as fast as the network at that
       * moment: measured from one machine on 2026-09-22 it ran 6839ms cold and
       * then about 260ms warm. The refresh still runs, so counts and prices stay
       * truthful when another tab changed the cart; it just no longer gates the
       * dialog. Before the V1.2 hardening pass this handler was a bare
       * drawer.showModal() with no request at all, which is the behaviour the
       * click is being restored to.
       */
      button.addEventListener('click', function () { open(button); refresh(); });
    });
    var closeButton = drawer.querySelector('[data-pf-cart-close]');
    if (closeButton) closeButton.addEventListener('click', close);
    drawer.addEventListener('click', function (event) { if (event.target === drawer) close(); });
    drawer.addEventListener('close', function () { if (lastFocus && lastFocus.focus) lastFocus.focus(); });

    // The terms box is an acknowledgement, not a gate (CHG-2026-029): the
    // checkout button is left enabled and the native checkout POST works with or
    // without JavaScript.
    var checkoutButton = drawer.querySelector('[data-pf-cart-checkout]');
    if (checkoutButton) checkoutButton.disabled = false;

    body.addEventListener('click', function (event) {
      var line = event.target.closest ? event.target.closest('[data-pf-line]') : null;
      if (!line) return;
      var key = line.getAttribute('data-line-key');
      var qtyNode = line.querySelector('[data-pf-line-qty]');
      var quantity = qtyNode ? Number(qtyNode.textContent) || 1 : 1;
      if (event.target.closest('[data-pf-line-plus]')) {
        changeLine(key, quantity + 1).catch(function (failure) { report(failure.message); });
        return;
      }
      if (event.target.closest('[data-pf-line-minus]')) {
        /* 1 is the floor now, not the tier rung: the price follows the tier the
           line was bought under, so walking the count down is a price decision
           the buyer is allowed to make rather than something to block. 0 stays
           reserved for Remove. */
        if (quantity <= 1) return;
        changeLine(key, quantity - 1).catch(function (failure) { report(failure.message); });
        return;
      }
      if (event.target.closest('[data-pf-line-remove]')) {
        changeLine(key, 0).catch(function (failure) { report(failure.message); });
        return;
      }
    });
  }

  window.PawfeederCart = { refresh: refresh, open: open, close: close, addItems: addItems, render: renderCart };
})();
